import {
  Button,
  Divider,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from '@material-ui/core'
import dayjs from 'dayjs'
import React, { Fragment, useState } from 'react'
import { useTranslation } from 'react-i18next'
import ConfigurationBreadcrumbs from '@/components/ConfigurationBreadcrumbsBar'
import Loading from '@/components/Loading'
import EmployeeCreationDialog from '@/domains/employees/components/EmployeeCreationDialog'
import styles from '@/domains/employees/components/EmployeesConfiguration.module.scss'
import EmployeesRow from '@/domains/employees/components/EmployeesRow'
import {
  useDeactivateEmployee,
  useGetActiveEmployees,
  useGetDeactivatedEmployees,
} from '@/domains/employees/hooks'
import { useConfirmMutationShowingErrors } from '@/hooks'

export function EmployeesTable() {
  const { t } = useTranslation()
  const [showCreationModal, setShowCreationModal] = useState(false)

  const { employees, queryResult } = useGetActiveEmployees()
  const { deactivatedEmployees, queryResult: deactivatedQueryResult } =
    useGetDeactivatedEmployees()

  const deactivateEmployee = useConfirmMutationShowingErrors({
    title: t('employee.confirm_deactivation'),
    actionText: t('deactivate'),
    successMessage: t('employee.employee_deactivated'),
    mutation: useDeactivateEmployee(),
  })

  const _handleDeactivateClick = async (id: string) => {
    deactivateEmployee({
      variables: {
        employeeId: id,
      },
      onCompleted: () => {
        queryResult.refetch()
        deactivatedQueryResult.refetch()
      },
    })
  }

  return (
    <Paper className={`${styles.root} u-mb`}>
      <ConfigurationBreadcrumbs modelName={'employees'} />
      <Typography variant="h5" gutterBottom>
        {t('employee.employees_configurations')}
      </Typography>
      {queryResult.loading ? (
        <Loading />
      ) : (
        <Fragment>
          <Table className="u-mb-sm" size="small">
            <TableHead>
              <TableRow>
                <TableCell>{t('id')}</TableCell>
                <TableCell align="right">{t('customer.email')}</TableCell>
                <TableCell align="right">{t('customer.firstname')}</TableCell>
                <TableCell align="right">{t('customer.lastname')}</TableCell>
                <TableCell align="right">
                  {t('customer.date_of_birth')}
                </TableCell>
                <TableCell align="right">{t('customer.phone')}</TableCell>
                <TableCell align="right">{t('customer.sex')}</TableCell>
                <TableCell align="right">{t('customer.role')}</TableCell>
                <TableCell align="right" />
              </TableRow>
            </TableHead>
            <TableBody>
              {Object.keys(employees).map((eKey) => (
                <EmployeesRow
                  key={eKey}
                  employee={employees[eKey]}
                  deactivateEmployee={() =>
                    _handleDeactivateClick(employees[eKey]._id)
                  }
                />
              ))}
            </TableBody>
          </Table>
          <Button
            className="u-mr-mini"
            variant="contained"
            onClick={() => setShowCreationModal(true)}
          >
            {t('employee.employee_create')}
          </Button>
          <EmployeeCreationDialog
            visible={showCreationModal}
            setVisible={setShowCreationModal}
          />
          {deactivatedEmployees.length > 0 && (
            <Fragment>
              <Divider style={{ marginTop: '3rem', marginBottom: '1.5rem' }} />
              <Typography variant="h6" gutterBottom color="textSecondary">
                {t('employee.deactivated_employees')}
              </Typography>
              <Table className="u-mb-sm" size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>{t('id')}</TableCell>
                    <TableCell align="right">{t('customer.email')}</TableCell>
                    <TableCell align="right">
                      {t('customer.firstname')}
                    </TableCell>
                    <TableCell align="right">
                      {t('customer.lastname')}
                    </TableCell>
                    <TableCell align="right">{t('customer.role')}</TableCell>
                    <TableCell align="right">
                      {t('employee.deactivated_at')}
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {deactivatedEmployees.map((employee) => (
                    <TableRow
                      key={employee._id}
                      className={styles.deactivatedRow}
                    >
                      <TableCell component="th" scope="row">
                        {employee._id}
                      </TableCell>
                      <TableCell align="right">{employee.email}</TableCell>
                      <TableCell align="right">{employee.firstname}</TableCell>
                      <TableCell align="right">{employee.lastname}</TableCell>
                      <TableCell align="right">
                        {employee.roles.join(' ')}
                      </TableCell>
                      <TableCell align="right">
                        {employee.deactivatedAt
                          ? dayjs(employee.deactivatedAt).format('DD.MM.YYYY')
                          : ''}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Fragment>
          )}
        </Fragment>
      )}
    </Paper>
  )
}

export default EmployeesTable
